'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Search, X, LogOut, Settings, User } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { TMDBMovie } from '../lib/tmdb';
import { useIsTVDevice } from '../hooks/useTVNavigation';
import { useTVNavigation as useTVNavigationContext } from '../context/TVNavigationContext';
import { useAuthStore } from '../lib/auth';
import { getApiUrl } from '../lib/api';

interface HeaderProps {
  onSearch?: (query: string) => void;
  onLogoClick?: () => void;
  onMovieClick?: (movie: TMDBMovie) => void;
}

export default function Header({ onSearch, onLogoClick }: HeaderProps) {
  const { user, authenticated, signOut, isAdmin } = useAuthStore();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const suggestionsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const signInButtonRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const confirmSignOutButtonRef = useRef<HTMLButtonElement>(null);
  
  // TV support
  const isTV = useIsTVDevice();
  
  // Register nodes with navigation system
  const { registerNode } = useTVNavigationContext();
  
  useEffect(() => {
    if (!isTV) return;
    
    // Register nodes with actual refs
    if (logoRef.current) {
      registerNode('logo', logoRef);
    }
    if (searchInputRef.current) {
      registerNode('search-input', searchInputRef);
    }
    // Register profile button (for authenticated) or sign-in button (for guests)
    if (authenticated && profileButtonRef.current) {
      registerNode('profile-button', profileButtonRef);
    } else if (!authenticated && signInButtonRef.current) {
      registerNode('profile-button', signInButtonRef);
    }
  }, [isTV, authenticated, registerNode]);
  
  // Update neighbors
  const { updateNodeNeighbors } = useTVNavigationContext();
  useEffect(() => {
    if (!isTV) return;
    
    updateNodeNeighbors('logo', {
      right: 'search-input',
      down: 'hero-play-button'
    });
    updateNodeNeighbors('search-input', {
      left: 'logo',
      right: 'profile-button',
      down: 'hero-play-button'
    });
    updateNodeNeighbors('profile-button', {
      left: 'search-input',
      down: 'hero-play-button'
    });
  }, [isTV, updateNodeNeighbors]);
  
  // Preload logo image with loading state
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setLogoLoaded(true);
    };
    img.onerror = () => {
      // Even if image fails to load, show the fallback
      setLogoLoaded(true);
    };
    img.src = '/logo.png';
  }, []);
  
  // Use ref to store the latest onSearch function
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  // Navigation is now handled by the global navigation system via useTVKeyboardNavigation

  // Handle profile dropdown toggle
  const toggleProfileDropdown = useCallback(() => {
    setIsProfileDropdownOpen(prev => !prev);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false);
      }
    };

    if (isProfileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileDropdownOpen]);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch search suggestions
  useEffect(() => {
    if (suggestionsTimeoutRef.current) {
      clearTimeout(suggestionsTimeoutRef.current);
    }

    if (searchQuery.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    suggestionsTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await fetch(
          getApiUrl('TMDB_SEARCH_SUGGESTIONS', undefined, { query: searchQuery })
        );
        const data = await response.json();
        if (data.success && data.suggestions) {
          setSuggestions(data.suggestions);
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error('Failed to fetch suggestions:', error);
        setSuggestions([]);
      }
    }, 300);

    return () => {
      if (suggestionsTimeoutRef.current) {
        clearTimeout(suggestionsTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Live TMDB search as user types - triggers main page results
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length === 0) {
      // Clear search when empty
      onSearchRef.current?.('');
      setShowSuggestions(false);
      return;
    }

    if (searchQuery.trim().length < 3) {
      return;
    }

    // Debounce and trigger main page search
    // Increased delay to 600ms to reduce unnecessary API calls while user is typing
    searchTimeoutRef.current = setTimeout(() => {
      if (searchQuery.trim()) {
        onSearchRef.current?.(searchQuery.trim());
        setShowSuggestions(false);
      }
    }, 600); // Debounce by 600ms

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Search happens live as user types
  };

  const handleSignOutClick = () => {
    setShowSignOutConfirm(true);
    setIsProfileDropdownOpen(false);
  };

  const handleSignOut = async () => {
    await signOut();
    setIsProfileDropdownOpen(false);
    setShowSignOutConfirm(false);
  };

  const handleCancelSignOut = () => {
    setShowSignOutConfirm(false);
  };

  const handleSignIn = () => {
    if (isTV) {
      navigate({ to: '/tv-activate' });
    } else {
      navigate({ to: '/auth/signin' });
    }
  };

  const getUserInitials = () => {
    if (!user?.email) return 'U';
    return user.email[0].toUpperCase();
  };

  // Auto-focus Cancel button when confirmation dialog opens on TV
  useEffect(() => {
    if (showSignOutConfirm && isTV && cancelButtonRef.current) {
      // Small delay to ensure dialog is rendered
      setTimeout(() => {
        cancelButtonRef.current?.focus();
      }, 100);
    }
  }, [showSignOutConfirm, isTV]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-black/95 border-b border-white/10 shadow-2xl' 
          : 'bg-gradient-to-b from-black/40 via-black/20 to-transparent'
      }`}
    >
      {/* Backdrop blur layer - separate from content */}
      <div className={`absolute inset-0 transition-all duration-500 ${
        scrolled ? 'backdrop-blur-md' : 'backdrop-blur-sm'
      }`}></div>
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="flex items-center justify-between h-16 lg:h-18">
          {/* Logo */}
          <div
            ref={logoRef}
            className={`flex-shrink-0 cursor-pointer group transition-all duration-300 hover:scale-105 ${isTV ? 'tv-focusable' : ''}`}
            onClick={onLogoClick}
            role="button"
            tabIndex={isTV ? 0 : -1}
            aria-label="Go to home"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onLogoClick?.();
              }
            }}
          >
            <div className="flex items-center gap-3">
              {/* Logo Image */}
              <div className="relative w-10 h-10 lg:w-12 lg:h-12 group-hover:rotate-3 transition-transform duration-300">
                <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 to-red-600/20 rounded-full group-hover:blur-sm transition-all duration-300"></div>
                
                {/* Loading Spinner */}
                {!logoLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-6 h-6 lg:w-8 lg:h-8 border-2 border-red-500/30 border-t-red-500 rounded-full animate-spin"></div>
                  </div>
                )}
                
                {/* Actual Logo */}
                <img 
                  src="/logo.png" 
                  alt="MoviesVault Logo" 
                  className={`relative w-full h-full object-contain drop-shadow-xl group-hover:drop-shadow-2xl transition-all duration-300 filter brightness-110 ${
                    logoLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  onLoad={() => setLogoLoaded(true)}
                  onError={() => setLogoLoaded(true)}
                />
              </div>
              
              {/* Text */}
              <h1 className="text-2xl lg:text-3xl font-extrabold tracking-tight group-hover:tracking-wider transition-all duration-300 whitespace-nowrap">
                <span className="text-white group-hover:text-gray-100 transition-colors duration-300">Movies</span>
                <span className="text-red-600 group-hover:text-red-500 transition-colors duration-300">Vault</span>
              </h1>
            </div>
          </div>

          {/* Center Search Bar - Always Visible */}
          <div className="flex-1 flex justify-center px-6 lg:px-8">
            <div ref={searchContainerRef} className="relative w-full max-w-xl">
              <form
                onSubmit={handleSearchSubmit}
                className="relative group"
                role="search"
                aria-label="Movie search"
              >
                <label htmlFor="movie-search" className="sr-only">
                  Search for movies
                </label>
                <div className="relative">
                  <input
                    ref={searchInputRef}
                    id="movie-search"
                    type="search"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => {
                      if (suggestions.length > 0) {
                        setShowSuggestions(true);
                      }
                    }}
                    onBlur={() => {
                      // Delay to allow click on suggestion
                      setTimeout(() => setShowSuggestions(false), 200);
                    }}
                    placeholder="Search movies..."
                    autoComplete="off"
                    tabIndex={isTV ? 0 : undefined}
                    className={`w-full px-5 py-3 pl-12 pr-12 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500/50 focus:bg-white/15 transition-all duration-300 shadow-lg group-hover:shadow-xl ${isTV ? 'text-lg lg:text-xl py-4' : 'text-sm lg:text-base'}`}
                    aria-label="Search movies"
                  />
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-hover:text-red-400 transition-colors duration-300 pointer-events-none" aria-hidden="true" />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery('');
                        onSearchRef.current?.(''); // Clear search
                        setSuggestions([]);
                        setShowSuggestions(false);
                      }}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 hover:bg-red-500/20 rounded-full p-1.5 transition-all duration-300 group"
                      aria-label="Clear search"
                    >
                      <X className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors duration-300" aria-hidden="true" />
                    </button>
                  )}
                </div>
                
                {/* Suggestions Dropdown */}
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-black/95 backdrop-blur-md border border-white/10 rounded-xl shadow-2xl z-50 max-h-64 overflow-y-auto">
                    {suggestions.map((suggestion, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => {
                          setSearchQuery(suggestion);
                          setShowSuggestions(false);
                          onSearchRef.current?.(suggestion);
                        }}
                        onMouseDown={(e) => e.preventDefault()} // Prevent blur
                        className="w-full text-left px-4 py-3 hover:bg-white/10 transition-colors duration-200 text-white text-sm first:rounded-t-xl last:rounded-b-xl"
                      >
                        <div className="flex items-center gap-2">
                          <Search className="w-4 h-4 text-gray-400" />
                          <span>{suggestion}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </form>
            </div>
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-3 lg:gap-4">
            {authenticated ? (
              /* Profile dropdown - Enhanced */
              <div className="relative" ref={profileDropdownRef}>
                <button 
                  ref={profileButtonRef}
                  className="focus:outline-none rounded-full relative group transition-all duration-300 hover:scale-105"
                  onClick={toggleProfileDropdown}
                  onKeyDown={(e) => {
                    if (isTV && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      e.stopPropagation(); // Prevent event from bubbling to global handlers
                      toggleProfileDropdown();
                    }
                  }}
                  tabIndex={isTV ? 0 : undefined}
                  aria-label="Open profile menu"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 to-red-600/20 rounded-full blur-sm group-hover:blur-md transition-all duration-300"></div>
                  <Avatar className="h-10 w-10 lg:h-12 lg:w-12 relative ring-2 ring-transparent group-hover:ring-red-500/50 transition-all duration-300 cursor-pointer shadow-lg group-hover:shadow-xl">
                    <AvatarImage src={user?.picture || undefined} className="object-cover" />
                    <AvatarFallback className="bg-gradient-to-br from-red-600 to-red-700 text-white font-bold text-sm lg:text-base shadow-inner">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                </button>
                
                {/* Enhanced dropdown menu */}
                {isProfileDropdownOpen && (
                  <div className="absolute right-0 top-full mt-3 w-80 bg-black/95 backdrop-blur-md border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
                    <div className="p-6">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-br from-red-500/30 to-red-600/30 rounded-full blur-sm"></div>
                          <Avatar className="h-14 w-14 relative ring-2 ring-red-500/50 shadow-lg">
                            <AvatarImage src={user?.picture || undefined} className="object-cover" />
                            <AvatarFallback className="bg-gradient-to-br from-red-600 to-red-700 text-white font-bold text-lg">
                              {getUserInitials()}
                            </AvatarFallback>
                          </Avatar>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-lg font-semibold text-white truncate">
                            {user?.name || 'User'}
                          </p>
                          <p className="text-sm text-gray-400 truncate">
                            {user?.email}
                          </p>
                        </div>
                      </div>
                      
                      <div className="border-t border-white/10 mb-4 pt-4">
                        {isAdmin && (
                          <button
                            onClick={() => {
                              navigate({ to: '/admin' });
                              setIsProfileDropdownOpen(false);
                            }}
                            className="w-full flex items-center gap-4 px-4 py-3 text-sm text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-all duration-300 mb-2 group"
                          >
                            <Settings className="h-5 w-5 group-hover:rotate-90 transition-transform duration-300" />
                            <span className="font-medium">Admin Dashboard</span>
                          </button>
                        )}
                        <button
                          onClick={handleSignOutClick}
                          onKeyDown={(e) => {
                            if (isTV && (e.key === 'Enter' || e.key === ' ')) {
                              e.preventDefault();
                              handleSignOutClick();
                            }
                          }}
                          className="w-full flex items-center gap-4 px-4 py-3 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all duration-300 group"
                        >
                          <LogOut className="h-5 w-5 group-hover:scale-110 transition-transform duration-300" />
                          <span className="font-medium">Sign Out</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Sign In Button for Guests */
              <button
                ref={signInButtonRef}
                onClick={handleSignIn}
                onKeyDown={(e) => {
                  if (isTV && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    e.stopPropagation(); // Prevent event from bubbling to global handlers
                    handleSignIn();
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-all duration-300 hover:scale-105 shadow-lg"
                tabIndex={isTV ? 0 : undefined}
                aria-label="Sign in"
              >
                <User className="h-4 w-4" />
                <span className="text-sm">Sign In</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Sign Out Confirmation Dialog */}
      <AnimatePresence>
        {showSignOutConfirm && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCancelSignOut}
              className="fixed inset-0 bg-black/90 z-50"
            />

            {/* Dialog */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={handleCancelSignOut}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="bg-[#141414] rounded-lg border border-gray-800 shadow-2xl max-w-md w-full p-8"
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  Sign Out
                </h2>
                <p className="text-gray-400 mb-8">
                  Are you sure you want to sign out?
                </p>

                <div className="flex gap-4 justify-end">
                  <button
                    ref={cancelButtonRef}
                    onClick={handleCancelSignOut}
                    onKeyDown={(e) => {
                      if (isTV) {
                        if (e.key === 'ArrowRight') {
                          e.preventDefault();
                          confirmSignOutButtonRef.current?.focus();
                        } else if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleCancelSignOut();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          handleCancelSignOut();
                        }
                      }
                    }}
                    className={`px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-all duration-300 ${
                      isTV ? 'tv-focusable-item' : ''
                    }`}
                    tabIndex={isTV ? 0 : undefined}
                  >
                    Cancel
                  </button>
                  <button
                    ref={confirmSignOutButtonRef}
                    onClick={handleSignOut}
                    onKeyDown={(e) => {
                      if (isTV) {
                        if (e.key === 'ArrowLeft') {
                          e.preventDefault();
                          cancelButtonRef.current?.focus();
                        } else if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSignOut();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          handleCancelSignOut();
                        }
                      }
                    }}
                    className={`px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-all duration-300 ${
                      isTV ? 'tv-focusable-item' : ''
                    }`}
                    tabIndex={isTV ? 0 : undefined}
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}